import { useEffect, useState } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import styles from "../styles/tables.module.scss";

import { If } from "../shared/components/control-statements/If";
import { Title } from "../shared/components/Title";
import { Empty } from "../shared/components/Empty";
import { Icon } from "../shared/components/core/Icon";
import { Button } from "../shared/components/Button";
import { Loading } from "../shared/components/Loading";

import {EnvironmentStorage} from "../shared/tools/EnvironmentStorage";
import { UserStorage } from "../shared/tools/UserStorage";
import ROLES from "../data/Roles";

import { EnvironmentsAPI } from "../providers/EnvironmentsAPI";
import { IntegrationsAPI } from "../providers/IntegrationsAPI"

import EcleticaTables from "../shared/containers/Tables/EcleticaTables";
import NetLogicTables from "../shared/containers/Tables/NetLogicTables";
import { Drawer } from "../shared/components/Drawer";
import { QRCode } from "../shared/tools/QRCode";
import { Label } from "../shared/components/Label";

dayjs.extend(duration);

const TABLE_PLATFORMS = {
	ECLETICA: "ECLETICA",
	NETLOGIC: "NETLOGIC",
};

const CARDAPIO_DIGITAL_CODE = "636172646170696F2D6469676974616C";

export default function Tables() {
	const [state, setState] = useState({
		loading: true,
		tableConfigurations: null,
		tables: []
	});
	const [catalogType, setCatalogType] = useState(null)
	const [showDigitalMenu, setShowDigitalMenu] = useState(false)
	const [loadingCreateDigitalMenu, setLoadingCreateDigitalMenu] = useState(false)
	const [digitalMenu, setDigitalMenu] = useState(null)
	const [digitalMenuQrcode, setDigitalMenuQrcode] = useState(null)

	const environment = EnvironmentStorage.getEnvironment();
	const userRole = UserStorage.getLoggedUser()?.role;
	const isSupport = userRole === ROLES.SUPPORT;

	useEffect(() => {
		const checkActivePlatform = async () => {
			const { id } = environment;
			const response = await EnvironmentsAPI.getConfigurationsModuleTables(id);
			const responseCatalog = response.getData({});
			setCatalogType(responseCatalog?.catalogType)
			setState(prevState => ({ ...prevState, tableConfigurations: responseCatalog?.table || null, loading: false }));
		};

		setState({ loading: true });
		checkActivePlatform();
	}, []);


	useEffect(() => {
		if (digitalMenu) generateQrCode(digitalMenu);
	}, [digitalMenu])

	const generateQrCode = async (table) => {
		const qrCodeData = await QRCode.getDataUrl(table.qrcode);
		setDigitalMenuQrcode(qrCodeData)
	};

	const loadTables = async () => {
		const { id } = environment;
		const response = await EnvironmentsAPI.getTablesEnvironment(id);
		const tables = response.getData([]);
		const filteredTables = tables.filter((t) => t.external_id !== CARDAPIO_DIGITAL_CODE)
		const digitalMenuTable = tables.filter((t) => t.external_id === CARDAPIO_DIGITAL_CODE)?.[0]

		setState(prevState => ({ ...prevState, tables: filteredTables }));
		setDigitalMenu(digitalMenuTable)
	};

	const syncEcletica = async () => {
		setState(prevState => ({ ...prevState, loading: true }));
		const { id } = environment;

		const responseTables = await IntegrationsAPI.postSyncTablesByEcletica(id);
		const responseCatalog = await IntegrationsAPI.postSyncCatalogEcletica(id);

		const tableErrors = responseTables.getErrors([]);
		const catalogErrors = responseCatalog.getErrors([]);
		const errors = [...tableErrors, ...catalogErrors];

		if (errors.length === 0) {
			await loadTables();
		} else {
			alert({ text: errors[0] });
		}
		setState(prevState => ({ ...prevState, loading: false }))
	};

	const activeOrDeactivateConfigs = async (active, platform) => {
		setState(prevState => ({ ...prevState, loading: true }));

		const newTableConfigurations = {
			...tableConfigurations,
			...(active !== undefined ? { active } : {}),
			...(platform !== undefined ? { platform }: {})
		};

		const { id } = environment;
		const value = {
			tables: { table: newTableConfigurations },
		};
		const response = await EnvironmentsAPI.putConfigurationsTables(id, value);

		if (response.getErrors([])?.length > 0) {
			alert({ text:"Erro ao alterar configurações de mesas" });
		}

		setState(prevState => ({ ...prevState, tableConfigurations: newTableConfigurations, loading: false }));
	};

	const { loading, tableConfigurations, tables, showCreateTable } = state;

	const active = tableConfigurations?.active || false;
	const platform = tableConfigurations?.platform || null;

	if (loading) {
		return <Loading fullContent={true} />;
	}

	const updateCatalogs = async (typeCatalog) => {
		const { id } = environment;
		const value = {
			tables: {
				catalogType: typeCatalog,
				table: {
					active: true,
					platform: TABLE_PLATFORMS.ECLETICA,
					
				}
			}
		};
			setState(prevState => ({ ...prevState, loading: true }));
		await EnvironmentsAPI.putConfigurationsTables(id, value);
		await loadTables();
		setCatalogType(typeCatalog)
		setState(prevState => ({ ...prevState, loading: false }));
	};

	const createDigitalMenu = async () => {
		setLoadingCreateDigitalMenu(true)

		const name = 'Cardápio Digital'
		const external_id = CARDAPIO_DIGITAL_CODE

		const value = { name, external_id };
		await EnvironmentsAPI.createTablesEnvironment(environment.id, value);
		
		setLoadingCreateDigitalMenu(false)
		setShowDigitalMenu(false)
		loadTables()
	};

	const downloadQrCode = () => {
		QRCode.downloadQRCodeImage(`qrcode-${digitalMenu.code}.png`, digitalMenu.qrcode);
	};

	const getSubtitle = () => {
		if (environment?.brand) {
			return `${environment.brand.name} - ${environment.name}` + (environment.active === false ? ' (Inativa)' : '')
		}
	};

	return (
		<>
			<Title
				title="Mesas"
				subtitle={getSubtitle()}
				backTo={{ url: "/configuracoes", name: "Configurações" }}
			>
				<If cond={active}>
					<div className={styles.title}>
						<Button design="default" onClick={() => setShowDigitalMenu(true)}>Cardápio Digital</Button>

						<If cond={platform === TABLE_PLATFORMS.ECLETICA}>
							<Button className="ml-20" design="default" onClick={() => syncEcletica()}>Sincronizar Mesas</Button>
						</If>

						<If cond={platform === TABLE_PLATFORMS.NETLOGIC}>
							<Button design="default" onClick={() => { setState(prevState => ({ ...prevState, showCreateTable: true })) }}>Adicionar Mesa</Button>
						</If>
						<If cond={platform === TABLE_PLATFORMS.ECLETICA && isSupport}>
							<Button design="default" onClick={() => updateCatalogs(catalogType === 'OLGA' ? 'ECLETICA' : 'OLGA')}>
								Ativar Cardápio {catalogType === 'OLGA' ? 'Ecletica' : 'Olga'}
							</Button>
						</If>
						<Button design="default" onClick={() => { activeOrDeactivateConfigs(false) }}>Desativar Mesas</Button>
					</div>
				</If>
			</Title>

			<If cond={active}>
				<If cond={!platform || platform === TABLE_PLATFORMS.ECLETICA}>
					<EcleticaTables tables={tables} loadTables={() => loadTables()} />
				</If>

				<If cond={platform && platform === TABLE_PLATFORMS.NETLOGIC}>
					<NetLogicTables
						tables={tables}
						loadTables={() => loadTables()}
						showCreateTable={showCreateTable}
						onCloseCreateTable={() => { setState(prevState => ({ ...prevState, showCreateTable: false })) }}
					/>
				</If>
			</If>

			<Drawer visible={showDigitalMenu} title="Cardápio Digital" onClose={() => setShowDigitalMenu(false)}>
				{!digitalMenu && (
					<>
						<Empty>
							<Icon name="exclamation-circle" size="4x" light />
							<p>Cardápio digital não criado.</p>
							<span>
								Os produtos do Cardápio Digital são somente para visualização.
							</span>
						</Empty>
						<div className={styles.footer}>
							<Button design="primary" className="w-100" onClick={createDigitalMenu} disabled={loadingCreateDigitalMenu} loading={loadingCreateDigitalMenu}>Criar Cardápio Digital</Button>
						</div>
					</>
				)}

				{digitalMenu && (
					<div className={styles.qrcode}>
						<Label>QrCode</Label>
						<div className={styles.qrcodeContainer}>
							<img
								src={digitalMenuQrcode}
								className={`${styles["img-fluid"]} ${styles.mobile}`}
								alt="Integrações"
								id="qrcode-image"
							/>
							<Button design="primary" onClick={downloadQrCode} className={"w-100"}>
								Salvar QR Code
							</Button>
						</div>
					</div>
				)}
			</Drawer>

			<If cond={!active}>
				<Empty>
					<Icon name="table-cells" size="4x" />
					<p>As mesas estão desativadas.</p>
					<span>
						Através desta ferramenta, você pode disponibilizar mesas para os
						seus clientes.
					</span>
					<Button design="primary" onClick={() => activeOrDeactivateConfigs(true, TABLE_PLATFORMS.ECLETICA)}>
						Ativar Eclética
					</Button>
					<Button className="mt-10" design="primary" onClick={() => activeOrDeactivateConfigs(true, TABLE_PLATFORMS.NETLOGIC)}>
						Ativar NetLogic
					</Button>
					<Button design="default" className="mt-10" onClick={() => setShowDigitalMenu(true)}>Cardápio Digital</Button>
				</Empty>
			</If>
		</>
	);

}
